/**
 * Domain types for XINGHAI-ZY (ARCHITECTURE.md §4, 设计方案 §2–§5, §12, §14).
 *
 * tri-state discipline (红线 14.4): `1` = present, `0` = confirmed absent,
 * `null` (or `UNLABELED = -100` for integer targets) = unlabeled / not applicable.
 * Unlabeled is NEVER coerced to a negative example.
 */

/** Sentinel for masked / not-applicable integer targets (ignored by the loss). */
export const UNLABELED = -100;

// --------------------------------------------------------------------------- //
// roles / tasks / domains
// --------------------------------------------------------------------------- //

/**
 * `teacher` manages classes (any class). The 师生 relation used for
 * conflict-of-interest still lives in `class_teacher`, kept in sync with a
 * `teacher` row in `class_member`. Display: annotator is 标注专家.
 */
export const ROLES = [
  "student",
  "annotator",
  "adjudicator",
  "teacher",
  "course_team",
  "admin",
] as const;
export type Role = (typeof ROLES)[number];

/** Roles an invitation link may grant (never `student` — students self-register). */
export const INVITABLE_ROLES = [
  "annotator",
  "adjudicator",
  "teacher",
  "course_team",
  "admin",
] as const;
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

/** §5 标注者方向 (B4 E1+E2 配对). */
export const ANNOTATOR_TRACKS = ["E1_phonetics", "E2_vocal", "E3_extra"] as const;
export type AnnotatorTrack = (typeof ANNOTATOR_TRACKS)[number];

/**
 * §1.5 师生回避策略.
 * - `exclude` — the class teacher never sees their own students (strict double blind);
 * - `review`  — they may annotate, but every such judgment is flagged
 *   (`teacher_of_speaker`) and the export carries a `TEACHER_STUDENT_OVERLAP` warning;
 * - `off`     — no special handling.
 */
export const AVOIDANCE_MODES = ["off", "exclude", "review"] as const;
export type AvoidanceMode = (typeof AVOIDANCE_MODES)[number];

/** §2.1 task matrix. Values are the manifest `task` strings. */
export const TASKS = [
  "T1_reference",
  "T1b_reference_singing",
  "T2_diagnostic",
  "T3_tongue_twister",
  "T4_passage",
  "T5_lyric_reading",
  "T6_singing",
  "T7_perception",
] as const;
export type Task = (typeof TASKS)[number];

/** Short code (T1/T1b/T2…) ↔ manifest task string. */
export const TASK_CODES = {
  T1: "T1_reference",
  T1b: "T1b_reference_singing",
  T2: "T2_diagnostic",
  T3: "T3_tongue_twister",
  T4: "T4_passage",
  T5: "T5_lyric_reading",
  T6: "T6_singing",
  T7: "T7_perception",
} as const satisfies Record<string, Task>;
export type TaskCode = keyof typeof TASK_CODES;

export const DOMAINS = ["reading", "singing"] as const;
export type Domain = (typeof DOMAINS)[number];

/** 红线 14.3: `induced` rows are product teaching material, never training data. */
export const ELICITATIONS = ["natural", "induced"] as const;
export type Elicitation = (typeof ELICITATIONS)[number];

export const MATERIAL_KINDS = [
  "diagnostic",
  "tongue_twister",
  "paragraph",
  "lyrics",
  "song",
  "reference",
  "perception",
] as const;
export type MaterialKind = (typeof MATERIAL_KINDS)[number];

// --------------------------------------------------------------------------- //
// marks & confusion classes
// --------------------------------------------------------------------------- //

/** §4.1 phone-level mark types (unified six). */
export const MARK_TYPES = [
  "correct",
  "substitution",
  "deletion",
  "insertion",
  "accented",
  "unrecognizable",
] as const;
export type MarkType = (typeof MARK_TYPES)[number];

/** §4.1 severity table. `insertion` is counted into the owning word event. */
export const MARK_SEVERITY: Record<MarkType, number | null> = {
  correct: 1.0,
  substitution: 0.15,
  accented: 0.6,
  deletion: 0.0,
  unrecognizable: 0.1,
  insertion: null,
};

/** §4.2 word-level mark dimensions — the 9-dim `word_mark_targets` vector. */
export const WORD_MARK_DIMS = [
  "initial_error",
  "final_error",
  "tone_error",
  "hesitation",
  "incomplete_unit",
  "code_switch",
  "foreign_word",
  "pronunciation_error",
  "other_error",
] as const;
export type WordMarkDim = (typeof WORD_MARK_DIMS)[number];

/** §4.3 utterance-level mark types (+ two Phase-A metadata types). */
export const UTTERANCE_MARK_TYPES = [
  "hesitation",
  "incomplete_unit",
  "code_switch",
  "foreign_word",
  "rhyme_return_error",
  "melody_text_misalign",
] as const;
export type UtteranceMarkType = (typeof UTTERANCE_MARK_TYPES)[number];

/** §2.3 the 14 confusion classes. */
export const CONFUSION_CLASSES = [
  "flat_retroflex",
  "nasal_coda",
  "n_l",
  "aspiration",
  "jqx",
  "f_h",
  "r_lateral",
  "diphthong_reduction",
  "compound_final",
  "erhua",
  "tone_value",
  "tone_sandhi",
  "rhyme_return",
  "onset_timing",
] as const;
export type ConfusionClass = (typeof CONFUSION_CLASSES)[number];

/** Human labels for the 14 confusion classes (spec §2.3). Codes like
 * "flat_retroflex:s_sh" are labelled by their class part. */
export const CONFUSION_CLASS_LABELS: Record<ConfusionClass, string> = {
  flat_retroflex: "平翘舌",
  nasal_coda: "前后鼻音",
  n_l: "n / l",
  aspiration: "送气 / 不送气",
  jqx: "j q x 与尖团",
  f_h: "f / h",
  r_lateral: "r / l、r / y",
  diphthong_reduction: "复元音动程",
  compound_final: "复合韵母",
  erhua: "儿化",
  tone_value: "声调调值",
  tone_sandhi: "变调",
  rhyme_return: "归韵",
  onset_timing: "字头时机",
};

/** Label for a confusion code, tolerating the "class:detail" form and unknown values. */
export function confusionLabel(code: string): string {
  const cls = code.split(":")[0] as ConfusionClass;
  return CONFUSION_CLASS_LABELS[cls] ?? code;
}

/** 附录 A 十三辙. */
export const ZHE_NAMES = [
  "发花",
  "梭波",
  "乜斜",
  "一七",
  "姑苏",
  "人辰",
  "中东",
  "怀来",
  "灰堆",
  "遥条",
  "由求",
  "言前",
  "江阳",
] as const;
export type Zhe = (typeof ZHE_NAMES)[number];

// --------------------------------------------------------------------------- //
// material / speaker
// --------------------------------------------------------------------------- //

export interface MaterialChar {
  char: string;
  /** 红线 14.5 — locked pinyin, G2P fallback forbidden. */
  pinyin_locked: string;
  /** Canonical initial phone code (`ZH_I_S`), `null` for zero-initial syllables. */
  initial: string | null;
  /** Canonical final phone code (`ZH_F_I`); `null` only for non-syllabic entries. */
  final: string | null;
  /** Canonical tone class 0–4 (0 = neutral); `null` when not applicable. */
  tone: number | null;
  zhe: Zhe | null;
  confusion_risk: string[];
}

/** The default collection every P0 corpus material belongs to. */
export const DEFAULT_COLLECTION = "corpus_v1";
/**
 * 杨爽老师的《星海正音小程序录音资料》（2026-09 校订版）。
 * `level` 是教材的三个大部分：1 基础训练（01–07）/ 2 辅音正音法（08–15）/
 * 3 十三辙归韵正音法（16–29）。
 */
export const AI_TEST_COLLECTION = "ai_test_v4";
/** 素材集在界面上的名字（发布卡片分组、素材库筛选都用它）. */
export const AI_TEST_TITLE = "星海正音小程序录音资料";
/**
 * Superseded revisions of the same material set. `seed:ai-test` retires every
 * one of them by the same rule: a row nothing references is deleted, a row that
 * still carries assignments or recordings is kept and frozen.
 */
export const AI_TEST_RETIRED_COLLECTIONS = ["ai_test_v1", "ai_test_v2", "ai_test_v3"] as const;
export const AI_TEST_LEVELS = [1, 2, 3] as const;
export type AiTestLevel = (typeof AI_TEST_LEVELS)[number];

/** 教材大部分的中文名，学生端与发布流程共用. */
export const AI_TEST_PART_LABEL: Record<number, string> = {
  1: "基础训练",
  2: "辅音正音",
  3: "十三辙归韵",
};

/** 发布卡片上的大部分名（带条目区间）. */
export const AI_TEST_PART_RANGE_LABEL: Record<number, string> = {
  1: "基础训练（01–07）",
  2: "辅音正音法（08–15）",
  3: "十三辙归韵（16–29）",
};

/**
 * Copyright status of a material's text. `null` = not assessed (the P0 corpus is
 * original or traditional throughout). Only `in_copyright` has consequences:
 * every exported row built from such a material carries a non-blocking
 * `LYRICS_IN_COPYRIGHT` audit warning.
 */
export const MATERIAL_RIGHTS = ["public_domain", "in_copyright"] as const;
export type MaterialRights = (typeof MATERIAL_RIGHTS)[number];

export interface Material {
  id: string; // TT-04 / DIAG-S-003 / SONG-03 / PARA-05
  kind: MaterialKind;
  task: Task;
  domain: Domain;
  text: string;
  chars: MaterialChar[];
  confusion_classes: string[];
  version: string;
  frozen: boolean;
  /** Which authored collection this row belongs to (`corpus_v1` / `ai_test_v1`). */
  collection?: string;
  /** AI 正音测试 只有 1|2|3；其余素材为 null. */
  level?: number | null;
  /** Human section inside a collection, e.g. `3.3 辅音·双唇音 b p`. */
  section?: string | null;
  /** Presentation / recording order inside (collection, level, section). */
  sort_key?: number;
  /**
   * Per-material override of the §9 22 s cap, clamped to `MAX_DURATION_CAP_MS`.
   * `null` = the default 22 s. Anything above 22 s never enters the training manifest.
   */
  max_duration_ms?: number | null;
  /**
   * Per-material override of how many takes an assignment needs. `null` = the
   * task rule (T3 = 慢速 + 正常两遍, T4 = 每句一条, 其余 1 条).
   */
  passes?: number | null;
  /** 著作权状态；`in_copyright` 的行导出时带 `LYRICS_IN_COPYRIGHT` 警告. */
  rights?: MaterialRights | null;
  /** 一句话说明：作者、卒年、为什么仍在保护期. */
  rights_note?: string | null;
  /** §1.2 共识 3 */
  functional_load?: "high" | "mid" | "low" | null;
  meaning_collision?: string | null;
  prescription_ids?: string[];
}

/**
 * §2.2 the eight dialect background groups offered on the self-registration form.
 * Same keys as the dispatch `DIALECT_PRIOR` map.
 */
export const DIALECT_GROUPS = [
  "北方官话",
  "西南官话",
  "吴语",
  "粤语",
  "闽语",
  "客家",
  "潮汕",
  "湘赣",
] as const;
export type DialectGroup = (typeof DIALECT_GROUPS)[number];

/** §2.2 训练水平. */
export const TRAINING_LEVELS = ["vocal", "non_vocal", "freshman"] as const;
export type TrainingLevel = (typeof TRAINING_LEVELS)[number];

export const GENDERS = ["F", "M", "other"] as const;
export type Gender = (typeof GENDERS)[number];

/** B6 — the consent text revision stored with every signature. Bump to force re-signing. */
export const CONSENT_VERSION = "v1";

/**
 * 标注成果授权书版本 — accepted once, when a staff invitation is redeemed.
 * The annotation work product is licensed to 广州叽喳科技有限公司, beneficiary 杨爽老师.
 * Bump to force everyone to accept the new revision.
 */
export const WORK_AGREEMENT_VERSION = "v1";

/** Roles whose invitation must carry an accepted 成果授权书. */
export const WORK_AGREEMENT_ROLES = ["annotator", "adjudicator", "teacher", "course_team"] as const;

/** 录音遍次 (T3 慢速/正常两遍). */
export const RECORDING_PASSES = ["slow", "normal"] as const;
export type RecordingPass = (typeof RECORDING_PASSES)[number];

/** §9 环境底噪要求 — SNR ≥ 45 dB (A7). */
export const MIN_SNR_DB = 45;

/** §9 三授权 (红线 14.6). */
export interface Consent {
  train: boolean;
  display: boolean;
  commercial: boolean;
  signed_at: string | null;
}

export interface Speaker {
  user_id: string;
  speaker_code: string; // XZ-S017
  dialect_l1: string[];
  training_level: string;
  class_id: string | null;
  consent: Consent;
  minor_guardian_signed: boolean;
}

// --------------------------------------------------------------------------- //
// batch / assignment / recording
// --------------------------------------------------------------------------- //

export const BATCH_STATES = ["draft", "published", "collecting", "annotating", "frozen"] as const;
export type BatchState = (typeof BATCH_STATES)[number];

export interface Batch {
  id: string; // P0-T2
  phase: string; // P0 / P1 / P2
  tasks: Task[];
  material_ids: string[];
  state: BatchState;
  /** B5 校准批：发给全部在岗专家，不合并、不进导出。 */
  calibration?: boolean;
  /** C1 复测轮次：0 = 基线，1 = 第 4 周，2 = 第 8 周。 */
  retest_round?: number;
  /** C1 —— 生成这一轮复测的基线批次 id。 */
  retest_of?: string | null;
}

export const ASSIGNMENT_STATES = ["todo", "recorded", "skipped"] as const;
export type AssignmentState = (typeof ASSIGNMENT_STATES)[number];

export interface Assignment {
  id: string;
  batch_id: string;
  speaker_id: string;
  material_id: string;
  task: Task;
  state: AssignmentState;
  /** T5↔T6 pairing group id. */
  pair_group: string | null;
}

export const RECORDING_STATES = [
  "uploaded",
  "qc_ok",
  "qc_reject",
  "annotating",
  "merged",
  "arbitrating",
  "final",
] as const;
export type RecordingState = (typeof RECORDING_STATES)[number];

export interface RecordingQc {
  clipping: boolean;
  silence_ratio: number | null;
  too_long: boolean;
  note?: string | null;
}

export interface Recording {
  id: string; // ZY-T2-S017-0003
  assignment_id: string | null;
  speaker_id: string;
  material_id: string;
  task: Task;
  domain: Domain;
  elicitation: Elicitation;
  audio_path: string;
  duration_ms: number | null;
  sample_rate: number | null;
  qc: RecordingQc | null;
  state: RecordingState;
  paired_with: string | null;
  retest_of: string | null;
  session_id: string | null;
  audio_start?: number;
  audio_end?: number;
}

// --------------------------------------------------------------------------- //
// judgment (one expert's independent, double-blind judgment of one recording)
// --------------------------------------------------------------------------- //

export interface CharJudgment {
  char_index: number;
  /** reading + singing: 声母质量 [0,1] | null */
  initial: number | null;
  /** reading + singing: 韵母质量 [0,1] | null */
  final: number | null;
  /** reading only. 红线 14.1: singing 恒 null. */
  tone: number | null;
  mark_type: MarkType | null;
  /** realized phone code / IPA (canonical→realized evidence, §4.1). */
  realized: string | null;
  /** 实现调类 0–4. 红线 14.1: singing 恒 null. */
  realized_tone: number | null;
  /** singing 字头 [0,1] | null */
  onset?: number | null;
  /** singing 字腹 [0,1] | null */
  nucleus?: number | null;
  /** singing 字尾 [0,1] | null */
  coda?: number | null;
  /** singing 实现辙位 (§4.3 rhyme_return_error) */
  zhe_realized?: Zhe | null;
  confusion_class?: string | null;
  note?: string | null;
}

/** §5.2 reading rubric — [0,1] normalized. */
export interface ReadingUtteranceJudgment {
  domain: "reading";
  accuracy: number | null;
  completeness: number | null;
  fluency: number | null;
  prosodic: number | null;
  total: number | null;
}

/** §5.3 singing rubric — [0,1] normalized. */
export interface SingingUtteranceJudgment {
  domain: "singing";
  onset: number | null;
  nucleus: number | null;
  coda: number | null;
  intelligibility: number | null;
  rhythm: number | null;
}

export type UtteranceJudgment = ReadingUtteranceJudgment | SingingUtteranceJudgment;

export interface Judgment {
  recording_id: string;
  annotator_id: string;
  domain: Domain;
  chars: CharJudgment[];
  utterance: UtteranceJudgment;
  /** §5.5 费力度 [0,1] */
  listening_effort: number | null;
  /** §5.5 口音度 [0,1] — 单列、不入总评 (红线 14.7) */
  accentedness: number | null;
  note?: string | null;
  submitted_at?: string | null;
  elapsed_ms?: number | null;
}

// --------------------------------------------------------------------------- //
// merge / arbitration (§5.4)
// --------------------------------------------------------------------------- //

export const RESOLUTIONS = ["mean", "discuss", "arbitrate", "masked", "partial_unlabeled"] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

export interface DimensionMerge {
  resolution: Resolution;
  value: number | null;
  expert: { a: number | null; b: number | null };
  delta: number | null;
}

export interface DiscreteMerge<T = string> {
  resolution: "agree" | "arbitrate" | "masked" | "partial_unlabeled";
  value: T | null;
  expert: { a: T | null; b: T | null };
}

/** Continuous dimension keys per domain. */
export const READING_CHAR_DIMS = ["initial", "final", "tone"] as const;
export const SINGING_CHAR_DIMS = ["onset", "nucleus", "coda"] as const;
export type CharDim = (typeof READING_CHAR_DIMS)[number] | (typeof SINGING_CHAR_DIMS)[number];

export const READING_UTTERANCE_DIMS = [
  "accuracy",
  "completeness",
  "fluency",
  "prosodic",
  "total",
] as const;
export const SINGING_UTTERANCE_DIMS = [
  "onset",
  "nucleus",
  "coda",
  "intelligibility",
  "rhythm",
] as const;

export interface CharMerge {
  char_index: number;
  /** Continuous per-dimension merge (initial/final/tone or onset/nucleus/coda). */
  dims: Partial<Record<CharDim, DimensionMerge>>;
  mark_type: DiscreteMerge<MarkType>;
  realized: DiscreteMerge<string>;
  realized_tone: DiscreteMerge<number>;
  zhe_realized?: DiscreteMerge<Zhe>;
  /** §5.2/§5.3 weighted char score; null when any required dim is unlabeled. */
  char_score: number | null;
  /** true when arbitration split → tri-state mask (红线 14.4). Never 0. */
  masked: boolean;
}

export interface ArbitrationRef {
  /** `null` = utterance level. */
  char_index: number | null;
  dimension: string;
}

export interface MergeResult {
  recording_id: string;
  domain: Domain;
  chars: CharMerge[];
  utterance: Record<string, DimensionMerge>;
  listening_effort: DimensionMerge;
  accentedness: DimensionMerge;
  needs_arbitration: ArbitrationRef[];
  masked_count: number;
  annotators: { a: string; b: string };
}

export interface ArbitrationDecision {
  recording_id?: string;
  /** `null` = utterance-level dimension. */
  char_index: number | null;
  dimension: string;
  adjudicator_id?: string;
  decision: "value" | "split";
  value?: number | string | null;
  note?: string | null;
}

// --------------------------------------------------------------------------- //
// perception / export
// --------------------------------------------------------------------------- //

export const TRIAL_TYPES = ["AX", "ABX", "ID"] as const;
export type TrialType = (typeof TRIAL_TYPES)[number];

export interface PerceptionTrial {
  id?: string;
  speaker_id: string;
  trial_type: TrialType;
  stimuli: string[];
  response: string | null;
  correct: boolean | null;
  rt_ms: number | null;
  confusion_class?: string | null;
  session_id?: string | null;
}

export interface ExportVersion {
  version: string; // 1.0.0
  recording_ids: string[];
  manifest_path: string;
  audit_report: AuditReport;
  frozen_at: string | null;
}

// --------------------------------------------------------------------------- //
// manifest / audit
// --------------------------------------------------------------------------- //

export interface ManifestMark {
  level: "phone" | "word" | "utterance";
  type: string;
  unit_index: number;
  canonical?: string | null;
  realized?: string | null;
  severity?: number | null;
  word_index?: number;
  start?: number;
  end?: number;
  source: string;
  source_label?: string;
  source_payload?: Record<string, unknown>;
}

export interface ManifestRow {
  audio_path: string;
  audio_start: number;
  audio_end: number;
  text: string;
  language: "zh";
  speaker: string;
  sample_weight: number;
  task: Task;
  domain: Domain;
  material_id: string;
  elicitation: Elicitation;
  phones: string[];
  phone_to_word: number[];
  words: string[];
  phone_scores: (number | null)[];
  phone_mark_targets: number[];
  realized_phones: string[];
  word_targets: (number | null)[][];
  word_mark_targets: number[][];
  tone_targets: (number | null)[];
  tone_class_targets: number[];
  utterance_targets: (number | null)[];
  listening_effort: number | null;
  accentedness: number | null;
  marks: ManifestMark[];
  expert_evidence: Record<string, unknown>;
  zhe_labels: (string | null)[];
  paired_with?: string | null;
  retest_of?: string | null;
  singing_rubric?: Record<string, number | null>;
  teacher_feedback?: string;
  course_level?: number;
}

export interface AuditFinding {
  level: "error" | "warning";
  code: string;
  recording_id: string;
  message: string;
}

export interface AuditReport {
  findings: AuditFinding[];
  ok: boolean;
}
